import { type App, ref, watch } from 'vue'
import AppModal from './app-modal.vue'
import AppModalContent from './app-modal-content.vue'
import AppModalDescription from './app-modal-description.vue'
import AppModalFooter from './app-modal-footer.vue'
import AppModalForm from './app-modal-form.vue'
import AppModalTitle from './app-modal-title.vue'

export function useAppModal<T extends unknown[]> (onBeforeOpen?: (...args: T) => void) {
  const modal = ref<AppModal>()
  // calling open with any arguments will be passed to onModalOpened callback
  const open = (...args: T) => {
    onBeforeOpen?.(...args)
    modal.value?.open()
  }
  const close = () => {
    modal.value?.close()
  }
  return { modal, open, close }
}

export function useModal () {
  const { disableOverflow, reenableOverflow } = useDocumentOverflow()
  const isOpen = ref(false)

  const open = () => {
    isOpen.value = true
  }
  const close = () => {
    isOpen.value = false
  }

  const onEscape = useEscListener(close)

  watch(isOpen, (value) => {
    if (value) {
      disableOverflow()
      window.addEventListener('keydown', onEscape)
    } else {
      reenableOverflow()
      window.removeEventListener('keydown', onEscape)
    }
  })

  return { isOpen, open, close }
}

const useDocumentOverflow = () => {
  const disableOverflow = () => {
    document.body.style.overflowY = 'hidden'
  }
  const reenableOverflow = () => {
    // delay because of vue lightbox
    setTimeout(() => {
      document.body.style.overflowY = ''
    }, 200)
  }
  return {
    disableOverflow,
    reenableOverflow,
  }
}

const useEscListener = (callback: () => void) => {
  return (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      callback()
    }
  }
}

export default {
  install (app: App) {
    app.component('AppModal', AppModal)
    app.component('AppModalContent', AppModalContent)
    app.component('AppModalDescription', AppModalDescription)
    app.component('AppModalForm', AppModalForm)
    app.component('AppModalFooter', AppModalFooter)
    app.component('AppModalTitle', AppModalTitle)
  },
}

declare module 'vue' {
  export interface GlobalComponents {
    AppModal: typeof AppModal,
    AppModalContent: typeof AppModalContent,
    AppModalDescription: typeof AppModalDescription,
    AppModalForm: typeof AppModalForm,
    AppModalFooter: typeof AppModalFooter,
    AppModalTitle: typeof AppModalTitle,
  }
}

declare global {
  type AppModal = InstanceType<typeof AppModal>
}
